import PropTypes from 'prop-types';
import React from 'react';

/* ========================================================================= */
/* Method(s) */
/* ========================================================================= */

/**
 * Reduce a fraction
 * src: https://stackoverflow.com/questions/4652468/is-there-a-javascript-function-that-reduces-a-fraction#answer-65927538
 * @param {*} numerator
 * @param {*} denominator
 * @returns {array}
 */
const reduce = (numerator, denominator) => {
    let a = numerator;
    let b = denominator;
    let c;
    while (b) {
        c = a % b; a = b; b = c;
    }
    return [numerator / a, denominator / a];
};

/**
 * Convert a set of grid layout options to fit an overall grid width (in columns)
 * Spacer cells will be removed first if the item is too wide for the grid
 * @param {object} options
 * @returns {object}
 */
const calculateCells = ({
    bottom,
    height,
    left,
    right,
    top,
    width,
    gridWidth
}) => {
    const actualWidth = Math.min(gridWidth, width);
    const actualHeight = Math.round((actualWidth / width) * height);
    const availableSpacerWidth = Math.max(0, gridWidth - actualWidth);

    const actualLeft = Math.min(left, Math.min(left, availableSpacerWidth));
    const actualRight = Math.min(right, Math.min(right, availableSpacerWidth - actualLeft));

    const totalCols = actualWidth + actualLeft + actualRight;
    const totalRows = actualHeight + top + bottom;
    const itemCells = actualWidth * actualHeight;
    const totalCells = totalCols * totalRows;
    const ratio = reduce(actualWidth, actualHeight);

    return {
        bottom,
        height: actualHeight,
        itemCells,
        left: actualLeft,
        ratio,
        right: actualRight,
        top,
        totalCells,
        totalCols,
        totalRows,
        width: actualWidth
    };
};

/**
 * Generate CSS for item cell for a given breakpoint
 * @param {object} cells
 * @param {string} breakpoint
 * @returns {string}
 */
const itemClasses = (cells, breakpoint = 'sm') => {
    const colClasses = `${breakpoint}:col-start-${cells.left + 1} ${breakpoint}:col-span-${cells.width}`;
    const rowClasses = `${breakpoint}:row-start-${cells.top + 1} ${breakpoint}:row-span-${cells.height}`;
    const aspectClasses = `${breakpoint}:aspect-${cells.ratio.join('/')}`;
    return `${colClasses} ${rowClasses} ${aspectClasses}`;
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * GridBlock layout component
 * Expects a grid context of grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const GridBlock = ({
    bottom = 1,
    children,
    className,
    height = 1,
    left = 1,
    right = 1,
    showOnMobile = true,
    spacerClassName,
    top = 1,
    width = 1
}) => {
    const cellOptions = {
        bottom,
        height,
        left,
        right,
        top,
        width
    };

    const sm = calculateCells({ ...cellOptions, gridWidth: 5 });
    const md = calculateCells({ ...cellOptions, gridWidth: 7 });
    const lg = calculateCells({ ...cellOptions, gridWidth: 9 });

    /* Inner grid */
    return (
        <div className={`${className || ''} ${!showOnMobile ? 'hidden' : ''} sm:grid grid-cols-subgrid col-span-3 row-span-1 sm:col-span-${sm.totalCols} sm:row-span-${sm.totalRows} md:col-span-${md.totalCols} md:row-span-${md.totalRows} lg:col-span-${lg.totalCols} lg:row-span-${lg.totalRows}`}>
            {/* Content cell */}
            <div className={`h-full relative ${itemClasses(sm, 'sm')} ${itemClasses(md, 'md')} ${itemClasses(lg, 'lg')}`}>
                {/* Content */}
                <div className='relative sm:absolute top-0 left-0 h-full w-full pr-1 pb-1'>
                    <div className='relative h-full w-full bg-foreground rounded overflow-hidden'>
                        {children}
                    </div>
                </div>
            </div>

            {/* Spacer cells */}
            {[...Array(lg.totalCells - lg.itemCells)].map((_, i) => {
                const showSm = i < (sm.totalCells - sm.itemCells) ? 'sm:block' : 'sm:hidden';
                const showMd = i < (md.totalCells - md.itemCells) ? 'md:block' : 'md:hidden';
                const showLg = i < (lg.totalCells - lg.itemCells) ? 'lg:block' : 'lg:hidden';

                return (
                    <div
                        className={`relative hidden sm:block aspect-1/1 ${showSm} ${showMd} ${showLg} ${spacerClassName || ''}`}
                        key={`spacer-${i}`}>
                        <div className='absolute top-0 bottom-0 left-0 right-0 mr-1 mb-1 bg-foreground rounded overflow-hidden' />
                    </div>
                );
            })}
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    bottom: PropTypes.number.isRequired,
    children: PropTypes.any,
    className: PropTypes.string,
    height: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
    right: PropTypes.number.isRequired,
    showOnMobile: PropTypes.bool,
    spacerClassName: PropTypes.string,
    top: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired
};

GridBlock.propTypes = propTypes;

export default GridBlock;
